/**
 * Edit dance page
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { danceApi, UpdateDanceRequest } from '../api/danceApi';
import { useDance } from '../hooks/useDances';

export function EditDancePage() {
  const { danceId } = useParams<{ danceId: string }>();
  const navigate = useNavigate();
  const { data: dance, isLoading } = useDance(danceId!);

  const [formData, setFormData] = useState<UpdateDanceRequest>({
    title: '',
    choreographer: '',
    formation: '',
    a1: '',
    a2: '',
    b1: '',
    b2: '',
    difficulty: undefined,
    style: undefined,
    notes: '',
    tags: [],
    callerNotes: '',
    teachingFigures: [],
    energyLevel: undefined,
    programRole: undefined,
    music: '',
    musicNotes: '',
    musicSource: '',
  });

  const [tagsInput, setTagsInput] = useState('');
  const [teachingInput, setTeachingInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const blocker = useBlocker(isDirty && !saving);

  useEffect(() => {
    if (dance) {
      setFormData({
        title: dance.title,
        choreographer: dance.choreographer,
        formation: dance.formation,
        a1: dance.a1,
        a2: dance.a2,
        b1: dance.b1,
        b2: dance.b2,
        difficulty: dance.difficulty,
        style: dance.style,
        notes: dance.notes,
        tags: dance.tags,
        callerNotes: dance.callerNotes,
        teachingFigures: dance.teachingFigures,
        energyLevel: dance.energyLevel,
        programRole: dance.programRole,
        music: dance.music,
        musicNotes: dance.musicNotes,
        musicSource: dance.musicSource,
      });
      setTagsInput(dance.tags?.join(', ') || '');
      setTeachingInput(dance.teachingFigures?.join(', ') || '');
    }
  }, [dance]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const updates = {
        ...formData,
        tags: tagsInput.split(',').map(tag => tag.trim()).filter(Boolean),
        teachingFigures: teachingInput.split(',').map(f => f.trim()).filter(Boolean),
      };
      await danceApi.updateDance(danceId!, updates);
      setIsDirty(false);
      navigate(`/dances/${danceId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update dance');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof UpdateDanceRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!dance) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Dance not found</div>;
  }

  const inputStyle = {
    width: '100%',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: 'var(--radius-sm)',
    transition: 'border-color var(--transition-base)',
    fontFamily: 'inherit'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--spacing-sm)',
    fontWeight: 'bold',
    color: 'var(--color-text)'
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <form onSubmit={handleSubmit} className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <h2 style={{ marginTop: 0, marginBottom: 'var(--spacing-lg)' }}>Edit Dance</h2>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          Title *
        </label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={handleChange('title')}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          Choreographer
        </label>
        <input
          type="text"
          value={formData.choreographer || ''}
          onChange={handleChange('choreographer')}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          Formation *
        </label>
        <input
          type="text"
          required
          value={formData.formation}
          onChange={handleChange('formation')}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          Difficulty
        </label>
        <select
          value={formData.difficulty || ''}
          onChange={handleChange('difficulty')}
          style={inputStyle}
        >
          <option value="">Not specified</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>Style</label>
        <select
          value={formData.style || ''}
          onChange={handleChange('style')}
          style={inputStyle}
        >
          <option value="">Not specified</option>
          <option value="contra">Contra</option>
          <option value="square">Square</option>
          <option value="english-country">English Country</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>Energy Level</label>
        <select
          value={formData.energyLevel || ''}
          onChange={(e) => { setIsDirty(true); setFormData(prev => ({ ...prev, energyLevel: e.target.value as any || undefined })); }}
          style={inputStyle}
        >
          <option value="">Not specified</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>Program Role</label>
        <select
          value={formData.programRole || ''}
          onChange={(e) => { setIsDirty(true); setFormData(prev => ({ ...prev, programRole: e.target.value as any || undefined })); }}
          style={inputStyle}
        >
          <option value="">Not specified</option>
          <option value="opener">Opener</option>
          <option value="filler">Filler</option>
          <option value="closer">Closer</option>
        </select>
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>Tags (comma-separated)</label>
        <input
          type="text"
          value={tagsInput}
          onChange={(e) => { setIsDirty(true); setTagsInput(e.target.value); }}
          placeholder="flowing, partner-heavy, beginner-friendly"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          A1 (16 beats) *
        </label>
        <textarea
          required
          value={formData.a1}
          onChange={handleChange('a1')}
          rows={3}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          A2 (16 beats) *
        </label>
        <textarea
          required
          value={formData.a2}
          onChange={handleChange('a2')}
          rows={3}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          B1 (16 beats) *
        </label>
        <textarea
          required
          value={formData.b1}
          onChange={handleChange('b1')}
          rows={3}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          B2 (16 beats) *
        </label>
        <textarea
          required
          value={formData.b2}
          onChange={handleChange('b2')}
          rows={3}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>
          Notes
        </label>
        <textarea
          value={formData.notes || ''}
          onChange={handleChange('notes')}
          rows={4}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>Caller Notes</label>
        <textarea
          value={formData.callerNotes || ''}
          onChange={handleChange('callerNotes')}
          rows={3}
          placeholder="Teaching tips, walk-through suggestions, sequencing notes..."
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={labelStyle}>Teaching Figures (comma-separated)</label>
        <input
          type="text"
          value={teachingInput}
          onChange={(e) => {
            setIsDirty(true);
            setTeachingInput(e.target.value);
            setFormData(prev => ({
              ...prev,
              teachingFigures: e.target.value.split(',').map(f => f.trim()).filter(Boolean)
            }));
          }}
          placeholder="balance & swing, robins chain"
          style={inputStyle}
        />
      </div>

      {formData.style === 'english-country' && (
        <div style={{
          marginBottom: 'var(--spacing-lg)',
          padding: 'var(--spacing-md)',
          backgroundColor: '#e8f4f8',
          borderRadius: 'var(--radius-sm)',
          borderLeft: '4px solid #17a2b8'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 'var(--spacing-md)', color: '#17a2b8' }}>Music (English Country)</div>
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={labelStyle}>Tune Name</label>
            <input
              type="text"
              value={formData.music || ''}
              onChange={handleChange('music')}
              placeholder="e.g. Nottingham Lace"
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={labelStyle}>Music Notes</label>
            <input
              type="text"
              value={formData.musicNotes || ''}
              onChange={handleChange('musicNotes')}
              placeholder="e.g. 32-bar jig, moderate tempo"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Music Source</label>
            <input
              type="text"
              value={formData.musicSource || ''}
              onChange={handleChange('musicSource')}
              placeholder="e.g. RSCDS Book 12, session tune"
              style={inputStyle}
            />
          </div>
        </div>
      )}

        {error && (
          <div style={{
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-lg)',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid var(--color-danger)'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>

          <button
            type="button"
            onClick={() => navigate(`/dances/${danceId}`)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>

        {blocker.state === 'blocked' && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white', padding: 'var(--spacing-xl)',
              borderRadius: 'var(--radius-md)', maxWidth: '400px', width: '90%'
            }}>
              <h3 style={{ marginTop: 0 }}>Unsaved Changes</h3>
              <p>You have unsaved changes. Are you sure you want to leave?</p>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
                <button onClick={() => blocker.reset?.()} className="btn btn-secondary">Stay</button>
                <button onClick={() => blocker.proceed?.()} className="btn btn-danger">Leave</button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
